import { latLngToCell, cellToLatLng, cellToBoundary, gridDisk, greatCircleDistance, UNITS } from "h3-js";

/** Average H3 edge length (km) per resolution — used to size the gridDisk ring count. */
const AVG_EDGE_KM: Record<number, number> = { 5: 8.544, 6: 3.229, 7: 1.22 };

/** Coarser-than-home display resolution so the tinted radius stays ~30–40 hexes
 *  across the 5–50 km slider range (res-8 would be ~1500 hexes at 15 km). */
export function radiusDisplayRes(radiusKm: number): number {
  if (radiusKm <= 8) return 7;
  if (radiusKm <= 20) return 6;
  return 5;
}

/** Display-resolution H3 cells whose center is within `radiusKm` of (lat,lng). */
export function cellsWithinRadius(lat: number, lng: number, radiusKm: number): string[] {
  const res = radiusDisplayRes(radiusKm);
  const origin = latLngToCell(lat, lng, res);
  const k = Math.ceil(radiusKm / AVG_EDGE_KM[res]) + 1;
  return gridDisk(origin, k).filter((c) => {
    const [clat, clng] = cellToLatLng(c);
    return greatCircleDistance([lat, lng], [clat, clng], UNITS.km) <= radiusKm;
  });
}

export interface RadiusFeature {
  type: "Feature";
  geometry: { type: "Polygon"; coordinates: number[][][] };
  /** `subject` is set on spread cones (spreadConesFC) so a cone tap can open its incident. */
  properties: { cell: string; subject?: string };
}
export interface RadiusFeatureCollection {
  type: "FeatureCollection";
  features: RadiusFeature[];
}

/** GeoJSON FeatureCollection of the radius cells, rings closed and in [lng,lat] order. */
export function radiusFeatureCollection(lat: number, lng: number, radiusKm: number): RadiusFeatureCollection {
  return {
    type: "FeatureCollection",
    features: cellsWithinRadius(lat, lng, radiusKm).map((cell) => {
      const ring = cellToBoundary(cell, true) as number[][]; // [[lng,lat],...] open ring
      return {
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [[...ring, ring[0]]] }, // close it
        properties: { cell },
      };
    }),
  };
}

/** [west, south, east, north] bounding box covering every highlighted radius cell,
 *  so the map can fit the whole watch area in view (all radius cells visible). */
export function radiusBounds(lat: number, lng: number, radiusKm: number): [number, number, number, number] {
  let west = 180, south = 90, east = -180, north = -90;
  for (const cell of cellsWithinRadius(lat, lng, radiusKm)) {
    for (const [clng, clat] of cellToBoundary(cell, true) as number[][]) {
      if (clng < west) west = clng;
      if (clng > east) east = clng;
      if (clat < south) south = clat;
      if (clat > north) north = clat;
    }
  }
  return [west, south, east, north];
}

/** GeoJSON polygons for an explicit list of H3 cells (the spread-cone pathCells),
 *  rings closed and in [lng,lat] order. Mirrors radiusFeatureCollection but takes the
 *  cells directly rather than deriving them from a radius. */
export function coneFeatureCollection(cells: string[]): RadiusFeatureCollection {
  return {
    type: "FeatureCollection",
    features: cells.filter(Boolean).map((cell) => {
      const ring = cellToBoundary(cell, true) as number[][]; // [[lng,lat],...] open ring
      return {
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [[...ring, ring[0]]] }, // close it
        properties: { cell },
      };
    }),
  };
}

/** Empty collection for seeding the map source before a radius is set. */
/** Spread cones for MANY incidents at once (the main Now map): each cell-polygon carries
 *  its incident `subject` so a cone tap can open that incident's detail. */
export function spreadConesFC(cones: Array<{ subject: string; cells: string[] }>): RadiusFeatureCollection {
  return {
    type: "FeatureCollection",
    features: cones.flatMap((c) =>
      c.cells.filter(Boolean).map((cell) => {
        const ring = cellToBoundary(cell, true) as number[][];
        return {
          type: "Feature" as const,
          geometry: { type: "Polygon" as const, coordinates: [[...ring, ring[0]]] },
          properties: { cell, subject: c.subject },
        };
      }),
    ),
  };
}

export const EMPTY_FC: RadiusFeatureCollection = { type: "FeatureCollection", features: [] };

const EARTH_KM = 6371.0088;
const DEG = Math.PI / 180;
const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

export const CONE_ANGLE_WIDE = 45;   // half-angle (deg) at confidence 0
export const CONE_ANGLE_TIGHT = 12;  // half-angle (deg) at confidence 1
export const TICK_STEP_MIN = 15;     // minutes between speed ticks
export const HORIZON_FALLBACK_MIN = 60;
export const ARC_POINTS = 12;        // arc resolution for the cone mouth
export const ARROWHEAD_FRAC = 0.16;  // arrowhead length as a fraction of reach
const ARROWHEAD_HALF_DEG = 7;        // arrowhead base half-angle
const TICK_MAX = 12;                 // cap tick count (slow speeds would otherwise crowd)

/** Great-circle forward: point `distKm` from (lat,lng) along `bearingDeg`. Returns [lat,lng]. */
export function destinationPoint(lat: number, lng: number, bearingDeg: number, distKm: number): [number, number] {
  const d = distKm / EARTH_KM;
  const br = bearingDeg * DEG;
  const lat1 = lat * DEG, lng1 = lng * DEG;
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(br));
  const lng2 = lng1 + Math.atan2(Math.sin(br) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
  return [lat2 / DEG, lng2 / DEG];
}

export interface ConeInput {
  apex: [number, number]; // [lat, lng]
  bearingDeg: number;
  speedKmh: number;
  confidence: number;
  horizonMinutes: number;
  /** Explicit reach (km), overriding speed·horizon. Used by the wind-prior cone, whose
   *  reach lives in windConeCells rather than a fire-speed scalar (stationary fires have
   *  no speedKmh). When set, the cone draws even with speedKmh 0. */
  reachKm?: number;
  /** Literal speed (km/h) for the time-to-reach ticks, independent of the outline reach.
   *  The wind-prior cone passes the real wind speed here so a tick at t minutes sits at
   *  speed·t — faster wind ⇒ wider-spaced ticks. Falls back to reach/horizon when absent. */
  tickSpeedKmh?: number;
}

/** Farthest great-circle distance (km) from `apex` ([lat,lng]) to any cell's centre.
 *  0 for an empty list. Derives a wind-prior cone's reach from its windConeCells. */
export function cellsReachKm(apex: [number, number], cells: string[]): number {
  let max = 0;
  for (const c of cells) {
    if (!c) continue;
    const [clat, clng] = cellToLatLng(c);
    const d = greatCircleDistance([apex[0], apex[1]], [clat, clng], UNITS.km);
    if (d > max) max = d;
  }
  return max;
}

export interface ConeFeature {
  type: "Feature";
  geometry:
    | { type: "Polygon"; coordinates: number[][][] }
    | { type: "LineString"; coordinates: number[][] };
  properties: { role: "wedge" | "shaft" | "arrowhead" | "tick"; subject?: string };
}

/** [lng,lat] of the point `km` from the apex along `bearing`. */
function ll(apex: [number, number], bearing: number, km: number): [number, number] {
  const [lat, lng] = destinationPoint(apex[0], apex[1], bearing, km);
  return [lng, lat];
}

/**
 * Analytic spread cone from scalar estimate fields. Apex at the incident origin,
 * fanning ±half-angle (wider when confidence is low) out to reach R = speed·horizon,
 * with a direction arrow and one tick chord per TICK_STEP_MIN of projected travel.
 * Returns null for a stationary estimate (no direction to draw).
 */
export function coneGeometry(input: ConeInput, subject?: string): ConeFeature[] | null {
  const { apex, bearingDeg, speedKmh, confidence } = input;
  const H = input.horizonMinutes > 0 ? input.horizonMinutes : HORIZON_FALLBACK_MIN;
  // Reach comes from an explicit reachKm (wind-prior cone) or speed·horizon (observed cone).
  const R = input.reachKm != null && input.reachKm > 0 ? input.reachKm : (speedKmh * H) / 60;
  if (!(R > 0)) return null; // no reach (truly stationary) → no cone to draw

  const alpha = CONE_ANGLE_WIDE + (CONE_ANGLE_TIGHT - CONE_ANGLE_WIDE) * clamp01(confidence);
  const props = (role: ConeFeature["properties"]["role"]): ConeFeature["properties"] => ({ role, subject });
  const apexLngLat: [number, number] = [apex[1], apex[0]];
  const features: ConeFeature[] = [];

  // Wedge: apex → arc across the mouth → back to apex.
  const arc: number[][] = [];
  for (let i = 0; i <= ARC_POINTS; i++) {
    const b = bearingDeg - alpha + (2 * alpha * i) / ARC_POINTS;
    arc.push(ll(apex, b, R));
  }
  features.push({ type: "Feature", geometry: { type: "Polygon", coordinates: [[apexLngLat, ...arc, apexLngLat]] }, properties: props("wedge") });

  // Shaft: apex → just short of the tip.
  const neckKm = R * (1 - ARROWHEAD_FRAC);
  features.push({ type: "Feature", geometry: { type: "LineString", coordinates: [apexLngLat, ll(apex, bearingDeg, neckKm)] }, properties: props("shaft") });

  // Arrowhead: filled triangle at the tip.
  features.push({
    type: "Feature",
    geometry: { type: "Polygon", coordinates: [[
      ll(apex, bearingDeg, R),
      ll(apex, bearingDeg - ARROWHEAD_HALF_DEG, neckKm),
      ll(apex, bearingDeg + ARROWHEAD_HALF_DEG, neckKm),
      ll(apex, bearingDeg, R),
    ]] },
    properties: props("arrowhead"),
  });

  // Ticks: chord across the cone at each projected arrival time. The tick speed is the
  // literal hazard speed when given (wind cone → real wind speed), else the reach/horizon
  // rate (observed cone → fire speed, since R = speed·H/60). A tick at t minutes sits at
  // speed·t/60 km, capped to the outline reach so ticks never spill past the cone mouth.
  const vTick = input.tickSpeedKmh != null && input.tickSpeedKmh > 0 ? input.tickSpeedKmh : (R * 60) / H;
  for (let t = TICK_STEP_MIN, n = 0; n < TICK_MAX; t += TICK_STEP_MIN, n++) {
    const r = (vTick * t) / 60;
    if (r > R + 1e-6) break;
    features.push({ type: "Feature", geometry: { type: "LineString", coordinates: [ll(apex, bearingDeg - alpha, r), ll(apex, bearingDeg + alpha, r)] }, properties: props("tick") });
  }

  return features;
}

export function spreadConesVectorFC(cones: Array<ConeInput & { subject: string }>): { type: "FeatureCollection"; features: ConeFeature[] } {
  return { type: "FeatureCollection", features: cones.flatMap((c) => coneGeometry(c, c.subject) ?? []) };
}

export function windConesVectorFC(cones: ConeInput[]): { type: "FeatureCollection"; features: ConeFeature[] } {
  return { type: "FeatureCollection", features: cones.flatMap((c) => coneGeometry(c) ?? []) };
}

export interface PointFeatureCollection {
  type: "FeatureCollection";
  features: Array<{ type: "Feature"; geometry: { type: "Point"; coordinates: [number, number] }; properties: Record<string, never> }>;
}

/** Single-point FC at (lat,lng) in [lng,lat] order — the live-position marker. */
export function livePointFC(lat: number, lng: number): PointFeatureCollection {
  return { type: "FeatureCollection", features: [{ type: "Feature", geometry: { type: "Point", coordinates: [lng, lat] }, properties: {} }] };
}

/** Combined ring polygons for several monitored places (home + saved). */
export function placesFeatureCollection(
  rings: Array<{ cell: string; radiusKm: number }>,
): RadiusFeatureCollection {
  const features: RadiusFeature[] = [];
  for (const r of rings) {
    if (!r.cell) continue;
    const [lat, lng] = cellToLatLng(r.cell);
    features.push(...radiusFeatureCollection(lat, lng, r.radiusKm).features);
  }
  return { type: "FeatureCollection", features };
}

/** One point marker per place, at its centre cell, in [lng,lat] order. */
export function placeMarkersFC(
  rings: Array<{ cell: string; radiusKm: number }>,
): PointFeatureCollection {
  return {
    type: "FeatureCollection",
    features: rings.filter((r) => r.cell).map((r) => {
      const [lat, lng] = cellToLatLng(r.cell);
      return { type: "Feature" as const, geometry: { type: "Point" as const, coordinates: [lng, lat] as [number, number] }, properties: {} };
    }),
  };
}

/** [w,s,e,n] union over every place's watch-zone bounds, plus the live travel ring
 *  when given. Returns null when there is nothing to frame. */
export function placesBounds(
  rings: Array<{ cell: string; radiusKm: number }>,
  live?: { lat: number; lng: number; radiusKm: number },
): [number, number, number, number] | null {
  let west = 180, south = 90, east = -180, north = -90;
  let any = false;
  const fold = (b: [number, number, number, number]) => {
    any = true;
    if (b[0] < west) west = b[0];
    if (b[1] < south) south = b[1];
    if (b[2] > east) east = b[2];
    if (b[3] > north) north = b[3];
  };
  for (const r of rings) {
    if (!r.cell) continue;
    const [lat, lng] = cellToLatLng(r.cell);
    fold(radiusBounds(lat, lng, r.radiusKm));
  }
  if (live) fold(radiusBounds(live.lat, live.lng, live.radiusKm));
  return any ? [west, south, east, north] : null;
}
